<template>
  <div>
    <div class="p-5 flex flex-col">
      <span class="font-semibold text-3xl">List Group Management</span>
      <table-top
        class="my-5"
        :page-size-start="false"
        @fetch="fetchGroups"
      >
        <template #start>
          <vs-button
            v-show="isUserCanAccessMenu(permission.name, 'add')"
            type="filled"
            color="primary"
            class="font-semibold"
            @click="toggleModal('add')"
          >
            Add Group
          </vs-button>
        </template>
      </table-top>
      <div
        v-show="isSwitching"
        class="px-5"
      >
        <vs-progress
          indeterminate
          color="secondary"
        />
      </div>
      <data-table
        :table-header="groupHeaders"
        :table-data="groupData"
        :sort-by="filter.sort_by"
        :sort-dir="filter.sort_dir"
        body-height="100vh"
        @sort="sortTable"
      >
        <template #action="{ row }">
          <div class="flex flex-row">
            <img
              v-show="isUserCanAccessMenu(permission.name, 'edit')"
              :src="require('../../../../assets/shipo-icon-edit.svg')"
              class="mr-1 icon-transparent-edit-bg"
              @click="editGroup(row)"
            >
            <img
              v-show="row.can_delete && isUserCanAccessMenu(permission.name, 'delete')"
              :src="require('../../../../assets/shipo-icon-delete.svg')"
              class="ml-1 icon-transparent-delete-bg"
              @click="toggleDeleteModal(row)"
            >
          </div>
        </template>
        <template #is_active="{ row }">
          <vs-switch
            v-if="row.can_delete"
            v-model="row.is_active"
            color="secondary"
            :disabled="isSwitching || !isUserCanAccessMenu(permission.name, 'is_active')"
          />
          <vs-switch
            v-else
            disabled
            v-model="row.is_active"
            color="primary"
            class="shadow-lg"
          >
            <span
              slot="on"
              class="font-semibold text-grey-001"
            >Active</span>
          </vs-switch>
        </template>
      </data-table>
      <table-bottom
        class="mt-5"
        @fetch="fetchGroups"
      />
    </div>
    <base-custom-modal :is-shown.sync="isModalShown">
      <template #title>
        <span class="font-semibold">{{ type === 'add' ? 'Add' : 'Edit' }} Group</span>
      </template>
      <template #body>
        <div class="flex flex-row">
          <div
            class="pr-3 flex flex-col"
            :class="[isGroupCanDelete ? 'w-3/4' : 'w-full' ]"
          >
            <span class="mb-3 font-semibold">Group Name</span>
            <vs-input
              v-model="form.name"
              placeholder="Enter group name"
              class="min-w-full"
            />
          </div>
          <div
            v-show="isGroupCanDelete"
            class="w-1/4 flex flex-col"
          >
            <span class="mb-3 font-semibold">Is Active</span>
            <div class="h-full flex flex-col justify-center">
              <vs-switch
                v-model="form.is_active"
                color="secondary"
                :disabled="!isUserCanAccessMenu(permission.name, 'is_active')"
              />
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="mt-5 flex flex-row justify-end">
          <vs-button
            type="border"
            color="secondary"
            class="mr-1 w-32 font-semibold"
            @click="toggleConfirmationModal"
          >
            Cancel
          </vs-button>
          <vs-button
            ref="submitButton"
            type="filled"
            color="secondary"
            class="ml-1 w-32 font-semibold vs-con-loading__container"
            @click="upsertGroup"
          >
            Submit
          </vs-button>
        </div>
      </template>
    </base-custom-modal>
    <confirmation-pop-up
      :active.sync="isConfirmationModalShown"
      @cancel="toggleConfirmationModal"
      @close="toggleConfirmationModal('close')"
    />
    <pop-up
      is-warning
      title="Are You Sure?"
      left-button="Delete"
      right-button="Cancel"
      :active.sync="isDeleteModalShown"
      @leftButton="deleteGroup"
      @rightButton="toggleDeleteModal"
      @close="toggleDeleteModal"
    >
      <div class="flex flex-col justify-center">
        <span>Do you really want to delete this record</span>
        <vs-progress
          indeterminate
          v-show="isProcessing"
          color="secondary"
          class="mt-5"
        />
      </div>
    </pop-up>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import BaseCustomModal from '@/components/baseCustomModal'
import ConfirmationPopUp from '@/components/confirmationPopUp'
import DataTable from '@/components/dataTable'
import PopUp from '@/components/popUp'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'

export default {
  components: {
    BaseCustomModal,
    ConfirmationPopUp,
    DataTable,
    PopUp,
    TableBottom,
    TableTop
  },
  data() {
    return {
      isModalShown: false,
      isConfirmationModalShown: false,
      isDeleteModalShown: false,
      isProcessing: false,
      isSwitching: false,
      isGroupCanDelete: true,
      type: 'add',
      filter: {
        sort_by: null,
        sort_dir: null
      },
      groupHeaders: [
        { key: 'action', label: 'Action' },
        { key: 'is_active', label: 'Is Active' },
        { key: 'name', label: 'Group Name', sortable: true }
      ],
      groupData: [],
      cloneGroupData: [],
      form: {
        name: null,
        is_active: false
      }
    }
  },
  computed: {
    ...mapGetters({
      groups: 'Group/groups'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    }
  },
  watch: {
    groups: {
      handler(val) {
        this.groupData = val
        this.cloneGroupData = this.cloneDeep(this.groupData)
      }
    },
    groupData: {
      deep: true,
      async handler(val) {
        const diff = this.differenceWith(val, this.cloneGroupData, this.isEqual)
        if (diff.length) {
          const item = diff[0]
          await this.switchGroupStatus(item)
        }
      }
    }
  },
  methods: {
    async fetchGroups() {
      try {
        await this.$store.dispatch('Group/fetchGroups', this.filter)
      } catch (err) {
        this.showFailedNotification(this, 'Cannot fetch groups, please try again')
      }
    },
    async upsertGroup() {
      try {
        this.isProcessing = true
        this.changeButtonState(this.$refs.submitButton.$el)

        await this.$store.dispatch(this.type === 'add'
          ? 'Group/postGroup'
          : 'Group/putGroup',
        this.form)

        this.isProcessing = false
        this.changeButtonState(this.$refs.submitButton.$el)
        this.isModalShown = false
        this.showSuccessNotification(this, `You have successfully ${this.type === 'add'
          ? 'create'
          : 'edit'
        } group`)
        this.resetForm()

        await this.fetchGroups()
      } catch ({ error }) {
        this.isProcessing = false
        this.changeButtonState(this.$refs.submitButton.$el)
        this.isModalShown = false
        this.showFailedNotification(this, error.errors[0].message)
      }
    },
    async switchGroupStatus(row) {
      try {
        this.isSwitching = true

        await this.$store.dispatch('Group/putGroup', {
          id: row.id,
          name: row.name,
          is_active: row.is_active
        })

        this.isSwitching = false
        this.showSuccessNotification(this, 'You have successfully update status')

        await this.fetchGroups()
      } catch (err) {
        this.isSwitching = false
        this.showFailedNotification(this, 'Cannot update status, please try again')
      }
    },
    async deleteGroup() {
      try {
        this.isProcessing = true

        await this.$store.dispatch('Group/deleteGroup', this.form.id)

        this.isProcessing = false
        this.isDeleteModalShown = false
        this.resetForm()

        await this.fetchGroups()
      } catch (err) {
        this.isProcessing = false
        this.isDeleteModalShown = false
        this.resetForm()
        this.showFailedNotification(this, 'Cannot delete this group, please try again')
      }
    },
    toggleModal(type) {
      this.type = type
      this.isModalShown = !this.isModalShown
    },
    toggleConfirmationModal(type = 'cancel') {
      if (type === 'close') {
        this.form = {
          name: null,
          is_active: false
        }
        this.isModalShown = false
        this.isConfirmationModalShown = false
        return
      }

      this.isModalShown = !this.isModalShown
      this.isConfirmationModalShown = !this.isConfirmationModalShown
    },
    toggleDeleteModal(item) {
      this.isDeleteModalShown = !this.isDeleteModalShown
      if (item) {
        this.form = {
          id: item.id,
          name: item.name,
          is_active: item.is_active
        }
        return
      }

      this.resetForm()
    },
    editGroup(item) {
      this.isGroupCanDelete = item.can_delete
      this.form = {
        id: item.id,
        name: item.name,
        is_active: item.is_active
      }
      this.toggleModal('edit')
    },
    resetForm() {
      this.isGroupCanDelete = true
      this.form = { name: null, is_active: false }
    },
    sortTable(sortBy, sortDir) {
      this.filter.sort_by = sortBy
      this.filter.sort_dir = sortDir

      this.fetchGroups()
    },
    changeButtonState(container) {
      if (this.isProcessing) {
        this.$vs.loading({
          background: 'secondary',
          color: '#FFF',
          container,
          scale: 0.35
        })
        return
      }

      this.$vs.loading.close(container)
    }
  }
}
</script>

<style lang="scss" scoped>
.icon-transparent-edit-bg {
  @apply rounded-lg cursor-pointer;
  @apply bg-primary;
}

.icon-transparent-delete-bg {
  @apply rounded-lg cursor-pointer;
  @apply bg-primary;
}

.vs-switch {
  &-secondary {
    &:disabled {
      opacity: 0.5 !important;
    }
  }

  &:disabled {
    opacity: 1;
  }
}

.vs-switch-primary.vs-switch-active {
  @apply w-20 #{!important};
  @apply bg-white;

  /deep/.vs-switch--circle {
    @apply bg-grey-002;
  }
}

.vs-switch-secondary.vs-switch-active {
  @apply bg-secondary-accent;

  /deep/.vs-switch--circle {
    @apply bg-secondary;
  }
}

/deep/.vs-switch--circle {
  @apply bg-grey-001;
}

/deep/.modal-content-container {
  @apply w-1/4 h-auto;
}

/deep/.vs-input--input {
  @apply bg-white;
  @apply h-12 rounded-lg #{!important};
  border: 0 solid transparent !important;
}
</style>
